import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { Button, FormControlLabel, Switch, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import Venue from 'types/models/Venue';
import { SupplierFormSchema } from './schemas/StockModuleForm';
import { StockSupplier } from 'types/models/StockReceipt';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { storeStockSupplier, updateStockSupplier } from 'services/supplierServices';
import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';
import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import SaveError from 'components/common/SaveError';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';

interface Props {
  supplier?: StockSupplier;
  isReadOnly?: boolean;
}

export default ({ supplier }: Props): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(true);

  React.useEffect(() => {
    let mounted = true;

    async function loadVenues() {
      setSaveError(null);

      try {
        const response = await getVenuesWithStockModuleActive();

        if (!mounted) return;

        setVenues(response?.data ?? []);
      } catch (error) {
        console.error(error);
        if (!mounted) return;
      } finally {
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessName: supplier?.businessName ?? '',
      taxId: supplier?.taxId ?? '',
      address: supplier?.address ?? '',
      phone: supplier?.phone ?? '',
      email: supplier?.email ?? '',
      venueIds: isVenueEditor ? [entity?.id] : supplier?.venues?.map((v) => v.id) ?? [],
      isActive: supplier?.isActive ?? true,
    },
    validationSchema: () => SupplierFormSchema(t),
    onSubmit: () => {
      setOpenDialog(true);
    },
  });

  const onConfirm = async () => {
    setSaveError(null);
    try {
      if (supplier) {
        await updateStockSupplier(+supplier.id, formik.values);
      } else {
        await storeStockSupplier(formik.values);
      }
      history.go(-1);
    } catch (error) {
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
      console.error(error);
    } finally {
      setOpenDialog(false);
      formik.setSubmitting(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        <h2>{t('stock.suppliers.title')}</h2>

        <TextField
          margin="dense"
          fullWidth
          name="businessName"
          label={t('stock.suppliers.form.business-name')}
          value={formik.values.businessName}
          onChange={formik.handleChange}
          error={formik.touched.businessName && Boolean(formik.errors.businessName)}
          helperText={formik.touched.businessName && formik.errors.businessName}
          disabled={formik.isSubmitting}
        />

        <TextField
          margin="dense"
          fullWidth
          name="taxId"
          label={t('stock.suppliers.form.tax-id')}
          value={formik.values.taxId}
          onChange={formik.handleChange}
          error={formik.touched.taxId && Boolean(formik.errors.taxId)}
          helperText={formik.touched.taxId && formik.errors.taxId}
          disabled={formik.isSubmitting}
        />

        <TextField
          margin="dense"
          fullWidth
          name="address"
          label={t('stock.suppliers.form.address')}
          value={formik.values.address}
          onChange={formik.handleChange}
          error={formik.touched.address && Boolean(formik.errors.address)}
          helperText={formik.touched.address && formik.errors.address}
          disabled={formik.isSubmitting}
        />

        <TextField
          margin="dense"
          fullWidth
          name="phone"
          label={t('stock.suppliers.form.phone')}
          value={formik.values.phone}
          onChange={formik.handleChange}
          error={formik.touched.phone && Boolean(formik.errors.phone)}
          helperText={formik.touched.phone && formik.errors.phone}
          disabled={formik.isSubmitting}
        />

        <TextField
          margin="dense"
          fullWidth
          name="email"
          label={t('stock.suppliers.form.email')}
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          disabled={formik.isSubmitting}
        />

        {!isVenueEditor && (
          <SelectField
            name="venueIds"
            label={t('stock.suppliers.form.venues')}
            placeholder={t('stock.suppliers.form.venues-placeholder')}
            value={formik.values.venueIds}
            options={mapToSelectOptions(venues, {
              label: 'name',
              value: 'id',
            })}
            isMulti
            isSearchable
            setFieldValue={formik.setFieldValue}
            touched={formik.touched.venueIds}
            error={formik.errors.venueIds}
            isDisabled={formik.isSubmitting}
            isClearable={true}
            backspaceRemovesValue
            onBlur={formik.handleBlur}
          />
        )}

        <div>
          <FormControlLabel
            name="isActive"
            control={<Switch checked={formik.values.isActive} onChange={formik.handleChange} />}
            label={t('stock.suppliers.form.is-active')}
            disabled={formik.isSubmitting}
          />
        </div>

        <SaveError error={saveError} />

        <div className="footer">
          <Button color="primary" variant="contained" type="submit" disabled={formik.isSubmitting} className="btn-50">
            {t('save')}
          </Button>
        </div>
      </form>
    </>
  );
};
